import React from "react";
import { User, Brain } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { ChatMessage } from "@/types/chat.types";
import ConfidenceBadge from "./ConfidenceBadge";
import CitationCard from "./CitationCard";
import DegradedSearchNotice from "./DegradedSearchNotice";
import NextSteps from "./NextSteps";

interface ChatMessageComponentProps {
  message: ChatMessage;
}

const ChatMessageComponent: React.FC<ChatMessageComponentProps> = ({
  message,
}) => {
  const isUser = message.role === "user";

  // Parse content and make citation markers clickable
  const renderContentWithCitations = (content: string) => {
    // Split by citation markers [1], [2], etc.
    const parts = content.split(/(\[\d+\])/g);

    return (
      <div className="text-sm text-text-muted leading-relaxed">
        {parts.map((part, index) => {
          // Check if this part is a citation marker like [1], [2]
          const citationMatch = part.match(/^\[(\d+)\]$/);

          if (citationMatch) {
            const citationNumber = parseInt(citationMatch[1], 10);
            return (
              <button
                key={index}
                onClick={() => {
                  const element = document.getElementById(
                    `citation-${citationNumber}`,
                  );
                  if (element) {
                    element.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                    });
                    // Add highlight effect
                    element.classList.add("ring-2", "ring-accent");
                    setTimeout(() => {
                      element.classList.remove("ring-2", "ring-accent");
                    }, 2000);
                  }
                }}
                className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-accent rounded hover:bg-accent-strong transition-colors mx-0.5 align-baseline cursor-pointer"
              >
                {citationNumber}
              </button>
            );
          }

          // Regular text - render with markdown support
          return (
            <ReactMarkdown
              key={index}
              components={{
                p: ({ children }) => <span>{children}</span>,
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
                em: ({ children }) => <em className="italic">{children}</em>,
                code: ({ children }) => (
                  <code className="bg-surface-2 px-1 py-0.5 rounded text-xs font-mono">
                    {children}
                  </code>
                ),
              }}
            >
              {part}
            </ReactMarkdown>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mb-6">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isUser ? "bg-surface-2 text-text-muted" : "bg-accent text-white"
            }`}
          >
            {isUser ? (
              <User className="w-5 h-5" />
            ) : (
              <Brain className="w-5 h-5" />
            )}
          </div>
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          <div
            className={`rounded-lg p-4 ${
              isUser
                ? "bg-surface-2 max-w-3xl"
                : "bg-surface border border-subtle"
            }`}
          >
            {isUser ? (
              <p className="text-sm text-text-muted leading-relaxed">
                {message.content}
              </p>
            ) : (
              <>
                {/* Confidence Badge + scope + reduced-mode / unverified-source notices */}
                {(message.confidence ||
                  message.scope ||
                  message.degraded ||
                  (message.unverifiedSources ?? 0) > 0) && (
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    {message.confidence && (
                      <ConfidenceBadge confidence={message.confidence} />
                    )}
                    {message.scope && (
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-surface-2 text-text-muted"
                        title="This answer was retrieved only from the listed collections"
                      >
                        Answered from:{" "}
                        {message.scope.collections
                          .map((c) => c.name)
                          .join(", ")}
                      </span>
                    )}
                    {message.degraded && <DegradedSearchNotice />}
                    {(message.unverifiedSources ?? 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-warn-bg text-warn"
                        title="The model referenced a source that couldn't be matched to the retrieved documents, so it was left out."
                      >
                        {message.unverifiedSources} source
                        {message.unverifiedSources === 1 ? "" : "s"} could not
                        be verified
                      </span>
                    )}
                  </div>
                )}

                {/* Answer with Citations */}
                {renderContentWithCitations(message.content)}

                {/* Actionable next steps when we couldn't answer (#24) */}
                <NextSteps steps={message.suggestedNextSteps} />
              </>
            )}
          </div>

          {/* Citation Cards (Only for Assistant Messages) */}
          {!isUser && message.citations && message.citations.length > 0 && (
            <div className="mt-4 space-y-3">
              <p className="text-xs font-medium text-text-muted mb-2">
                Sources:
              </p>
              {message.citations.map((citation) => (
                <CitationCard
                  key={citation.citationNumber}
                  citation={citation}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessageComponent;
